//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import ChipInput, { ChipRendererArgs } from "material-ui-chip-input";
import * as React from "react";
import Autosuggest, {
  ChangeEvent,
  RenderSuggestionParams,
  RenderSuggestionsContainerParams,
  SuggestionHighlightedParams,
  SuggestionSelectedEventData,
  SuggestionsFetchRequestedParams,
} from "react-autosuggest";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import { debounce, throttle } from "throttle-debounce";
// feed-scheduler-input imports
import { feedSchedulerInputFragments, feedSchedulerInputQuery } from "./FeedSchedulerInput.gql";
import * as s from "./FeedSchedulerInput.styles";
import {
  IAttendee,
  IAttendeeSection,
  IAttendeeSuggestion,
  IContainer,
  IProps,
  IState,
} from "./FeedSchedulerInputTypes";
// other imports
import { InfoTip } from "#webapp/src/components/tool-tip";
import { UserImage } from "#webapp/src/components/user-image";
// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// util
import { isValidEmail } from "#webapp/src/util/email.util";
import { niceNameArrayFromPersons } from "#webapp/src/util/event.util";
import { logger } from "#webapp/src/util/logger.util";
// material-ui
import { H2 } from "#webapp/src/components/ui";
import { EventAvailable, People } from "@clockwise/design-system/icons";
import { Chip, FormControl, FormHelperText, MenuItem, Paper, Popper } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
const MAX_ATTENDEES = 15;

class FeedSchedulerInputCmpt extends React.Component<IProps, IState> {
  private onUpdateInputDebounced: Function;
  private onUpdateInputThrottled: Function;

  constructor(props: IProps) {
    super(props);
    this.state = {
      queryInput: "",
      attendees: props.initialPersons,
      inputError: "",
      cachedViewer: props.viewer,
      suggestions: [],
      highlightedSuggestion: null,
      anchorEl: null,
    };
    this.props.onChange(props.initialPersons.map((a) => a.primaryCalendar as string));

    // throttling and debouncing search
    this.onUpdateInputDebounced = debounce(500, this.onUpdateInputRefetch);
    this.onUpdateInputThrottled = throttle(500, this.onUpdateInputRefetch);
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { viewer } = nextProps;
    if (viewer && viewer.groupQueryResult && viewer.personQueryResultErrorable) {
      this.setState({
        cachedViewer: viewer,
        suggestions: this.generateSuggestions(viewer),
      });
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onUpdateInputRefetch(queryInput: string, excludeCalendars: string[]) {
    this.props.relay.refetch({ queryInput, excludeCalendars });
  }

  private validateAttendeesMaxLength = (newAttendeesCount: number) => {
    if (this.state.attendees.length + newAttendeesCount > MAX_ATTENDEES) {
      return `Can't add more than 15 people.`;
    }
    return "";
  };

  private validateEmailText = (email?: string) => {
    const maxAttendeesError = this.validateAttendeesMaxLength(email ? 1 : 0);
    if (maxAttendeesError) {
      return maxAttendeesError;
    }
    if (!email || email.length === 0) {
      return "Please enter an email";
    }
    const emailValidation = isValidEmail(email);
    if (!emailValidation.validCharacters) {
      return "Please check your email for invalid characters";
    } else if (!emailValidation.validFormat) {
      return "Please check your email format";
    } else {
      return "";
    }
  };

  private getPersonsFromViewer = (viewer: ISchema.IViewer | null) => {
    return (
      (viewer &&
        viewer.personQueryResultErrorable &&
        viewer.personQueryResultErrorable.__typename === "PersonQueryResult" &&
        viewer.personQueryResultErrorable.persons) ||
      []
    );
  };

  private getGroupsFromViewer = (viewer: ISchema.IViewer | null) => {
    return (
      (viewer &&
        viewer.groupQueryResult &&
        viewer.groupQueryResult.__typename === "ExternalGroupSearchResult" &&
        viewer.groupQueryResult.groups) ||
      []
    );
  };

  private getTeamsFromViewer = (viewer: ISchema.IViewer | null) => {
    return (
      (viewer &&
        viewer.teamQueryResultErrorable &&
        viewer.teamQueryResultErrorable.__typename === "TeamQueryResult" &&
        viewer.teamQueryResultErrorable.teams) ||
      []
    );
  };

  private getTeamMembersFromTeamQueryResult = (viewer: ISchema.IViewer | null) => {
    return (
      (viewer &&
        viewer.teamQueryResultErrorable &&
        viewer.teamQueryResultErrorable.__typename === "TeamQueryResult" &&
        viewer.teamQueryResultErrorable.teamMembers) ||
      []
    );
  };

  private getTeammatesForTeam = (team: ISchema.ITeam) => {
    const teammates: IAttendee[] = [];
    [...team.teamMembers, ...team.invitedMembers].forEach((teamMember) => {
      if (!teamMember.person.isYou) {
        teammates.push({
          primaryEmail: teamMember.person.primaryCalendarId,
          primaryCalendar: teamMember.person.primaryCalendarId,
          profile: teamMember.person.profile,
        });
      }
    });

    return teammates;
  };

  private getTeammatesForTeamFromTeamQueryResult = (teamId: string) => {
    const { viewer } = this.props;

    const teams = this.getTeamsFromViewer(viewer);
    const teamMembers = this.getTeamMembersFromTeamQueryResult(viewer);

    const team = teams.find((team) => team.teamId === teamId);
    if (!team) {
      logger.error("Could not find team", teamId);
      return [];
    }

    const teammates: IAttendee[] = [];

    team.teamRoleAndPersonIds.forEach((t) => {
      const teamMember = teamMembers.find((tm) => tm.person.personId === t.personId);
      if (teamMember && !teamMember.person.isYou) {
        teammates.push({
          primaryEmail: teamMember.person.primaryCalendarId,
          primaryCalendar: teamMember.person.primaryCalendarId,
          profile: teamMember.person.profile,
        });
      }
    });

    return teammates;
  };

  // ~-~-~-~-~-~-~-
  // Chip Helpers
  // ~-~-~-~-~-~-~-
  private addAttendees = (newAttendees: IAttendee[]) => {
    if (!this.props.org) {
      return logger.error("Could not add attendees because no org");
    }

    // dedupe attendees
    const attendeesToAdd = newAttendees.filter((newAttendee) => {
      return (
        this.state.attendees.findIndex((a) => a.primaryCalendar === newAttendee.primaryCalendar) ===
        -1
      );
    });

    // set state
    const attendees = this.state.attendees.concat(attendeesToAdd);
    this.setState({ attendees, inputError: "", queryInput: "" });

    // onChange + refetch callbacks
    this.props.onChange(attendees.map((a) => a.primaryCalendar as string));
    this.props.relay.refetch({
      queryInput: "",
      orgRelayId: this.props.org.id,
      excludeCalendars: attendees.map((a) => a.primaryCalendar as string),
    });
  };

  private addCalendarGroups = (calendarGroups: IAttendee[]) => {
    if (!this.props.org) {
      return logger.error("Could not add calendar groups because no org");
    }

    // call refetch to grab the members associated with the groupCalendarId and add those
    this.props.relay.refetch(
      {
        queryInput: this.state.queryInput,
        orgRelayId: this.props.org.id,
        excludeCalendars: this.state.attendees.map((a) => a.primaryCalendar as string),
        emails: calendarGroups.map((group) => group.primaryCalendar as string),
        externalGroupRefetch: true,
      },
      undefined,
      () => {
        if (this.props.org && this.props.org.externalGroups.__typename === "ExternalGroupList") {
          const attendees: IAttendee[] = [];
          this.props.org.externalGroups.list.forEach((eg) =>
            eg.memberEmails.forEach((primaryEmail) =>
              attendees.push({ primaryEmail, primaryCalendar: primaryEmail }),
            ),
          );

          // validate max length before adding full group
          const maxAttendeesError = this.validateAttendeesMaxLength(attendees.length);
          this.setState({ inputError: maxAttendeesError, suggestions: [] });
          if (!maxAttendeesError) {
            this.addAttendees(attendees);
          }
        } else {
          this.addAttendees(calendarGroups);
        }
      },
    );
  };

  private addTeam = (teamId: string) => {
    if (!this.props.org) {
      return logger.error("Could not add team because no org");
    }

    // call refetch to grab the teammates associated with the teamId and add those
    this.props.relay.refetch(
      {
        queryInput: this.state.queryInput,
        orgRelayId: this.props.org.id,
        excludeCalendars: this.state.attendees.map((a) => a.primaryCalendar as string),
        teamId: teamId,
        teamAttendeesRefetch: true,
      },
      undefined,
      () => {
        if (
          this.props.org &&
          this.props.org.teamForTeamId &&
          this.props.org.teamForTeamId.__typename === "Team"
        ) {
          // validate max length before adding full team
          const attendees = this.getTeammatesForTeam(this.props.org.teamForTeamId);
          const maxAttendeesError = this.validateAttendeesMaxLength(attendees.length);
          this.setState({ inputError: maxAttendeesError, suggestions: [] });
          if (!maxAttendeesError) {
            this.addAttendees(attendees);
          }
        } else {
          logger.error("failed to fetch attendees for selected teamId");
          this.addAttendees(this.getTeammatesForTeamFromTeamQueryResult(teamId));
        }
      },
      { force: true },
    );
  };

  private onDeleteChip = (index: number) => {
    const attendees = this.state.attendees.slice(0);
    attendees.splice(index, 1);

    // set state and callback
    this.setState({ attendees });
    this.props.onChange(attendees.map((a) => a.primaryCalendar as string));
  };

  // ~-~-~-~-~-~-~-
  // Chip Input Helpers
  // ~-~-~-~-~-~-~-
  private onUpdateInput = (_event: React.FormEvent<any>, params: ChangeEvent) => {
    // set state
    this.setState({ queryInput: params.newValue, inputError: "" }, () => {
      if (params.method !== "type") {
        return; // no need to fetch new data if the user is just pressing on up/down or selecting a suggestion from the list of suggestions
      }

      // throttle or debounce
      const excludeCalendars = this.state.attendees.map((a) => a.primaryCalendar as string);
      if (params.newValue.length < 5) {
        this.onUpdateInputThrottled(params.newValue, excludeCalendars);
      } else {
        this.onUpdateInputDebounced(params.newValue, excludeCalendars);
      }
    });
  };

  // this function exists to manage handling the raw input string from the chip input textfield
  private onChipInputSubmit = (queryInput: string) => {
    if (this.state.highlightedSuggestion !== null) {
      return; // because something is highlighted, we do not need to handle adding raw input
    }

    const inputError = this.validateEmailText(queryInput);
    this.setState({ inputError });
    if (!inputError) {
      return this.addAttendees([{ primaryEmail: queryInput, primaryCalendar: queryInput }]);
    }
  };

  private onInputFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    this.setState({ anchorEl: e.currentTarget });
  };

  private onInputBlur = () => {
    this.setState({
      inputError: this.state.queryInput ? this.validateEmailText(this.state.queryInput) : "",
    });
  };

  // ~-~-~-~-~-~-~-
  // Autosuggest Helpers
  // ~-~-~-~-~-~-~-
  private onSuggestionsFetchRequested = (_params: SuggestionsFetchRequestedParams) => {
    this.setState({ suggestions: this.generateSuggestions(this.state.cachedViewer) });
  };

  private onSuggestionsClearRequested = () => {
    this.setState({ suggestions: [] });
  };

  private onSuggestionHighlighted = (params: SuggestionHighlightedParams) => {
    this.setState({ highlightedSuggestion: params.suggestion });
  };

  private onSuggestionSelected = (
    _event: React.FormEvent<HTMLDivElement>,
    data: SuggestionSelectedEventData<IAttendeeSuggestion>,
  ) => {
    // validate max length before adding attendee
    const attendeeCount = data.suggestion.type === "TEAM" ? 1 : data.suggestion.value.length;
    const maxAttendeesError = this.validateAttendeesMaxLength(attendeeCount);
    this.setState({ inputError: maxAttendeesError, suggestions: [] });
    if (maxAttendeesError) {
      return;
    }

    // add
    switch (data.suggestion.type) {
      case "PERSON":
      case "GCAL_DATA":
        this.addAttendees(data.suggestion.value);
        break;
      case "TEAM":
        this.addTeam(data.suggestion.value[0].primaryCalendar as string);
        break;
      case "GROUP":
      default:
        this.addCalendarGroups(data.suggestion.value);
        break;
    }
  };

  private getSectionSuggestions = (section: IAttendeeSection) => {
    return section.sectionSuggestions;
  };

  private getSuggestionValue = (suggestion: IAttendeeSuggestion) => {
    return suggestion.label;
  };

  private generateSuggestions = (viewer: ISchema.IViewer | null): IAttendeeSection[] => {
    // persons
    const personsQueryData: IAttendeeSuggestion[] = this.getPersonsFromViewer(viewer).map(
      (person) => {
        return {
          type: "PERSON",
          label: `${person.profile.givenName} ${person.profile.familyName}`,
          value: [person],
        };
      },
    );

    // groups
    const groupQueryData: IAttendeeSuggestion[] = this.getGroupsFromViewer(viewer).map((group) => {
      return {
        type: "GROUP",
        label: group.emailAddress,
        value: [{ primaryEmail: group.emailAddress, primaryCalendar: group.emailAddress }],
      };
    });

    // teams
    const teamQueryData: IAttendeeSuggestion[] = this.getTeamsFromViewer(viewer).map((team) => {
      // hack for now. probably should actually use a better data structure to hold what are now generic suggestions
      return {
        type: "TEAM",
        label: team.teamName,
        value: [{ primaryEmail: team.teamId, primaryCalendar: team.teamId }],
      };
    });

    // calendars selected in gcal ui
    const selectedGCalPersons: IAttendeeSuggestion[] = [];
    // only show if gcal checked persons and no chips added and nothing has been typed in yet
    if (
      this.props.selectedPersons.length > 0 &&
      this.state.attendees.length < 1 &&
      !this.state.queryInput
    ) {
      const persons: ISchema.IPerson[] = [];
      const nonPersonCalendarIds: string[] = [];
      this.props.selectedPersons.map((p) =>
        p.profile
          ? persons.push(p as ISchema.IPerson)
          : nonPersonCalendarIds.push(p.primaryCalendar as string),
      );

      selectedGCalPersons.push({
        type: "GCAL_DATA",
        label: niceNameArrayFromPersons(
          persons,
          nonPersonCalendarIds,
          25,
          true,
          undefined,
          true,
        ).join(""),
        value: [
          ...persons,
          ...nonPersonCalendarIds.map((calendarId) => ({
            primaryEmail: calendarId,
            primaryCalendar: calendarId,
          })),
        ],
      });
    }

    return [
      { sectionTitle: "People", sectionSuggestions: personsQueryData },
      { sectionTitle: "Groups", sectionSuggestions: groupQueryData },
      { sectionTitle: "Teams", sectionSuggestions: teamQueryData },
      { sectionTitle: "Your selected calendars", sectionSuggestions: selectedGCalPersons },
    ].filter((section) => section.sectionSuggestions.length > 0);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderChip = (args: ChipRendererArgs, key: any) => {
    const { chip, isFocused, handleClick, className } = args;

    const attendee = this.state.attendees.find((a) => a.primaryCalendar === chip) || chip;
    const displayName = attendee.profile
      ? `${attendee.profile.givenName} ${attendee.profile.familyName}`
      : chip;

    const targetElement = (
      <Chip // hover on chip, see email
        key={key}
        label={displayName}
        onClick={handleClick}
        onDelete={(_event) => this.onDeleteChip(key)}
        className={className}
        classes={{
          root: isFocused ? this.props.classes.chipFocused : "",
        }}
        cw-id="attendee-chip"
      />
    );

    if (attendee.profile) {
      return <InfoTip targetElement={targetElement} text={chip} tipType={"info"} />;
    }

    return targetElement;
  };

  private renderInputComponent = (inputProps: any) => {
    const { onChange, value, ...otherInputProps } = inputProps;
    return (
      <FormControl fullWidth error={!!this.state.inputError}>
        <ChipInput
          {...otherInputProps}
          blurBehavior="ignore"
          chipRenderer={this.renderChip}
          onDelete={(_chip, index) => this.onDeleteChip(index)}
          onUpdateInput={onChange}
          onBeforeAdd={this.onChipInputSubmit}
          newChipKeyCodes={[13, 9]} // enter, tab
          inputValue={value}
          value={this.state.attendees.map((a) => a.primaryCalendar)}
          classes={{
            inputRoot: this.props.classes.chipInput,
          }}
          cw-id="attendees-chip-input"
        />
        {this.state.inputError ? <FormHelperText>{this.state.inputError}</FormHelperText> : ""}
      </FormControl>
    );
  };

  private renderSuggestionsContainer = (params: RenderSuggestionsContainerParams) => {
    const { anchorEl } = this.state;
    return (
      <Popper
        style={{
          zIndex: 80,
        }}
        key={this.state.suggestions.length}
        anchorEl={anchorEl}
        open={!!params.children}
      >
        <Paper
          {...params.containerProps}
          style={{ width: anchorEl ? anchorEl.clientWidth : undefined }}
          square
        >
          {params.children}
        </Paper>
      </Popper>
    );
  };

  private renderSectionTitle = (section: IAttendeeSection) => {
    return <H2 style={s.styles.sectionTitle}>{section.sectionTitle}</H2>;
  };

  private renderSuggestion = (suggestion: IAttendeeSuggestion, params: RenderSuggestionParams) => {
    const { viewer } = this.props;
    switch (suggestion.type) {
      case "PERSON":
        const person = suggestion.value[0] as ISchema.IPerson;
        return (
          <MenuItem
            key={suggestion.label}
            classes={{
              root: params.isHighlighted ? this.props.classes.suggestionHighlighted : undefined,
            }}
            cw-id="suggested-attendee-person"
          >
            <div style={s.menuItem.content}>
              <UserImage
                style={s.menuItem.avatar}
                imgUrl={person.profile.externalImageUrl}
                name={person.profile.givenName}
              />
              <div style={s.menuItem.text}>
                <div style={s.menuItem.name}>
                  {person.profile.givenName} {person.profile.familyName}
                </div>
                <div style={s.menuItem.email}>{person.primaryEmail}</div>
              </div>
            </div>
          </MenuItem>
        );
      case "GROUP":
        const group = suggestion.value[0];
        return (
          <MenuItem
            key={suggestion.label}
            classes={{
              root: params.isHighlighted ? this.props.classes.suggestionHighlighted : undefined,
            }}
            cw-id="suggested-attendee-group"
          >
            <div style={s.menuItem.content}>
              <People style={s.menuItem.avatar} />
              <div style={s.menuItem.text}>
                <div style={s.menuItem.name}>{group.primaryEmail}</div>
              </div>
            </div>
          </MenuItem>
        );
      case "TEAM":
        const teamIdWrapper = suggestion.value[0]; // primaryEmail is the teamId
        const team = this.getTeamsFromViewer(viewer).find(
          (t) => t.teamId === teamIdWrapper.primaryEmail,
        );

        return (
          <MenuItem
            key={suggestion.label}
            classes={{
              root: params.isHighlighted ? this.props.classes.suggestionHighlighted : undefined,
            }}
            cw-id="suggested-attendee-team"
          >
            <div style={s.menuItem.content}>
              <People style={s.menuItem.avatar} />
              {this.getTeammatesForTeamFromTeamQueryResult(teamIdWrapper.primaryCalendar)
                .slice(0, 8)
                .map((p) => {
                  return (
                    <UserImage
                      key={p.primaryEmail}
                      style={s.styles.avatar}
                      imgUrl={p.profile ? p.profile.externalImageUrl : ""}
                      name={p.profile ? p.profile.givenName : p.primaryEmail}
                    />
                  );
                })}
              <div style={s.menuItem.text}>
                <div style={s.menuItem.name}>
                  {team ? team.teamName : teamIdWrapper.primaryEmail}
                </div>
              </div>
            </div>
          </MenuItem>
        );
      case "GCAL_DATA":
      default:
        return (
          <MenuItem
            key={suggestion.label}
            classes={{
              root: params.isHighlighted ? this.props.classes.suggestionHighlighted : undefined,
            }}
          >
            <div style={{ maxWidth: 230, overflow: "hidden", margin: "5px 0" }}>
              <div style={s.menuItem.content}>
                <EventAvailable {...s.calendar} />
                {this.props.selectedPersons
                  .slice(0, 8) // grab first few to render
                  .map((p) => {
                    return (
                      <UserImage
                        key={p.primaryEmail}
                        style={s.styles.avatar}
                        imgUrl={p.profile ? p.profile.externalImageUrl : ""}
                        name={p.profile ? p.profile.givenName : p.primaryEmail}
                      />
                    );
                  })}
              </div>
              <div style={s.menuItem.names}>{suggestion.label}</div>
            </div>
          </MenuItem>
        );
    }
  };

  public render() {
    const { classes } = this.props;

    return (
      <div className={classes.container}>
        <Autosuggest
          cw-id="attendees-input"
          multiSection
          highlightFirstSuggestion
          suggestions={this.state.suggestions as any} // HACK: the type should be IAttendeeSection, but it's fighting me for some reason..
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionHighlighted={this.onSuggestionHighlighted}
          renderSuggestionsContainer={this.renderSuggestionsContainer}
          renderSectionTitle={this.renderSectionTitle}
          renderSuggestion={this.renderSuggestion}
          renderInputComponent={this.renderInputComponent}
          getSectionSuggestions={this.getSectionSuggestions}
          getSuggestionValue={this.getSuggestionValue}
          onSuggestionSelected={this.onSuggestionSelected}
          inputProps={{
            label: "Add attendees",
            onBlur: this.onInputBlur,
            onChange: this.onUpdateInput,
            onFocus: this.onInputFocus,
            value: this.state.queryInput,
          }}
          theme={{
            suggestionsContainer: classes.suggestionsContainer,
            suggestionsList: classes.suggestionsList,
            suggestion: classes.suggestion,
          }}
        />
      </div>
    );
  }
}

/////////////////
// With Styles
/////////////////
export const FeedSchedulerInputStyled = withStyles(s.styles)(FeedSchedulerInputCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const FeedSchedulerInputRedux = connect(mapStateToProps, {})(FeedSchedulerInputStyled);

//////////////////
// RELAY
//////////////////
export const FeedSchedulerInput = createRefetchContainer<IContainer>(
  FeedSchedulerInputRedux,
  feedSchedulerInputFragments,
  feedSchedulerInputQuery,
);
